// components/Sender/SenderLocalRunsTab.js
import React, { useEffect, useRef, useState } from "react";
import { SenderApi } from "./SenderApi";
import { SenderResumeDialog } from "./SenderResumeDialog";
import { RunLogDialog } from "./RunLogDialog";

const STATUS_STYLE = {
  pending:  { label: "⏳ Создана",           bg: "#f3f4f6", fg: "#6b7280" },
  running:  { label: "▶ Выполняется",        bg: "#dbeafe", fg: "#1d4ed8" },
  stopping: { label: "⏹ Останавливается",    bg: "#fef3c7", fg: "#b45309" },
  finished: { label: "✅ Завершён",          bg: "#dcfce7", fg: "#166534" },
  stopped:  { label: "⏹ Остановлен",        bg: "#fef3c7", fg: "#b45309" },
  error:    { label: "✖ Ошибка",             bg: "#fee2e2", fg: "#b91c1c" },
};

export function SenderLocalRunsTab({
  selectedRunId, setSelectedRunId, onShowPlan, status, add, runtimes,
}) {
  const [runs, setRuns] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [resumeInfo, setResumeInfo] = useState(null);
  const [pendingStartId, setPendingStartId] = useState(null);
  const [logRunId, setLogRunId] = useState(null);

  const selectedRef = useRef(selectedRunId);
  useEffect(() => { selectedRef.current = selectedRunId; }, [selectedRunId]);

  useEffect(() => {
    if (offline) { setLoading(false); return; }
    let mounted = true;
    let inFlight = false;

    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const list = await SenderApi.listLocalRuns();
        if (!mounted) return;
        setRuns(list || []);
        setOffline(false);
        if (!selectedRef.current && (list || []).length) {
          setSelectedRunId(list[0].id);
        }
      } catch {
        if (mounted) setOffline(true);
      } finally {
        inFlight = false;
        if (mounted) setLoading(false);
      }
    };

    tick();
    const id = setInterval(tick, 2000);
    return () => { mounted = false; clearInterval(id); };
  }, [offline]);

  useEffect(() => {
    if (!selectedRunId || offline) { setProgress(null); return; }
    let mounted = true;
    let inFlight = false;

    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const p = await SenderApi.getRunProgress(selectedRunId);
        if (mounted) setProgress(p);
      } catch { /* */ } finally {
        inFlight = false;
      }
    };

    tick();
    const id = setInterval(tick, 1500);
    return () => { mounted = false; clearInterval(id); };
  }, [selectedRunId, offline]);

  const retry = () => {
    setOffline(false);
    setLoading(false);
  };

  const selected = runs.find((r) => r.id === selectedRunId) || null;
  const logRun = runs.find((r) => r.id === logRunId) || null;

  const doStart = async (runId, mode) => {
    try {
      await SenderApi.startLocalRun(runId, { progress_mode: mode });
      add?.(mode === "resume" ? "▶ Продолжаем" : "▶ Запуск стартовал",
           "success");
    } catch (e) {
      add?.("Ошибка старта: " + e.message, "error");
    }
  };

  const handleStartClick = async () => {
    if (!selected) return;
    if (status?.current_run_id && status.current_run_id !== selected.id) {
      add?.("Уже выполняется другой run. Остановите его сначала.", "warn");
      return;
    }
    let prog = null;
    try { prog = await SenderApi.getRunProgress(selected.id); } catch { /* */ }
    const hasProgress = prog && (
      (prog.run_sent ?? 0) > 0 ||
      Object.keys(prog.block_limits || {}).length > 0
    );
    if (hasProgress) {
      setResumeInfo(prog);
      setPendingStartId(selected.id);
      return;
    }
    await doStart(selected.id, "fresh");
  };

  const handleResumeChoice = async (choice) => {
    setResumeInfo(null);
    const runId = pendingStartId;
    setPendingStartId(null);
    if (!choice || !runId) return;
    if (choice === "fresh") {
      try { await SenderApi.resetLocalProgress(runId); } catch { /* */ }
    }
    await doStart(runId, choice);
  };

  const handleStop = async () => {
    if (!selected) return;
    try {
      await SenderApi.stopLocalRun(selected.id);
      add?.("⏹ Запрошена остановка", "info");
    } catch (e) {
      add?.("Ошибка стопа: " + e.message, "error");
    }
  };

  const handleReset = async () => {
    if (!selected) return;
    if (!window.confirm("Очистить прогресс этого запуска?")) return;
    try {
      await SenderApi.resetLocalProgress(selected.id);
      add?.("♻️ Прогресс очищен", "success");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Удалить запуск «${selected.template_name || selected.id}»?`)) return;
    try {
      await SenderApi.deleteLocalRun(selected.id);
      add?.("🗑 Запуск удалён", "success");
      setSelectedRunId(null);
      setProgress(null);
      setOffline(false);
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  


  const p = progress || {};
  const isActive = selected &&
    (selected.status === "running" || selected.status === "stopping");

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr",
                  gap: 12, minHeight: 480 }}>
      <div className="section-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid #e5e7eb",
                      display: "flex", justifyContent: "space-between",
                      alignItems: "center" }}>
          <div style={{ fontWeight: 700 }}>📋 Локальные запуски</div>
          <button className="btn-cancel"
                  style={{ fontSize: 12, padding: "4px 10px" }}
                  onClick={retry}>🔄</button>
        </div>

        {offline && (
          <div style={{
            padding: "10px 14px", background: "#fef3c7",
            color: "#92400e", fontSize: 12, lineHeight: 1.5,
            borderBottom: "1px solid #fde68a",
          }}>
            ⚠️ Локальный бекенд (127.0.0.1:17873) не отвечает.
            <button className="btn-cancel"
                    style={{ marginLeft: 8, padding: "2px 8px", fontSize: 11 }}
                    onClick={retry}>
              Повторить
            </button>
          </div>
        )}

        <div style={{ maxHeight: "calc(90vh - 100px)", overflowY: "auto" }}>
          {loading ? (
            <div style={{ padding: 20, color: "#9ca3af" }}>Загрузка…</div>
          ) : runs.length === 0 ? (
            <div style={{ padding: 20, color: "#9ca3af", fontSize: 13 }}>
              Пока нет запусков.<br /><br />
              Создайте: 📦 Шаблоны → 📋 Создать
            </div>
          ) : (
            runs.map((r) => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
              const isSel = r.id === selectedRunId;
              const isCurr = status?.current_run_id === r.id;
              return (
                <div key={r.id}
                     onClick={() => setSelectedRunId(r.id)}
                     style={{
                       padding: "10px 14px", cursor: "pointer",
                       borderBottom: "1px solid #f3f4f6",
                       background: isSel ? "#eff6ff" : "#fff",
                       borderLeft: isCurr ? "3px solid #3b82f6" : "none",
                     }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                                alignItems: "center", gap: 6 }}>
                    <div style={{ fontWeight: 700, fontSize: 13,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap" }}>
                      {isCurr && <span style={{ color: "#3b82f6" }}>▶ </span>}
                      {r.template_name || r.template_id || "—"}
                    </div>
                    <span style={{
                      padding: "2px 8px", borderRadius: 6,
                      fontSize: 10, fontWeight: 700,
                      background: st.bg, color: st.fg,
                    }}>{st.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4,
                                fontFamily: "Consolas, monospace" }}>
                    {r.id}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="section-card" style={{ overflowY: "auto" }}>
        {!selected ? (
          <div style={{ color: "#9ca3af", padding: 30, textAlign: "center" }}>
            Выбери запуск слева
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                🚀 {selected.template_name || selected.template_id}
              </h3>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                id: <code>{selected.id}</code>
                {selected.created_at && (
                  <> · создан:{" "}
                    {new Date(selected.created_at).toLocaleString("ru-RU")}
                  </>
                )}
                {selected.started_at && (
                  <> · старт:{" "}
                    {new Date(selected.started_at).toLocaleString("ru-RU")}
                  </>
                )}
                {selected.finished_at && (
                  <> · финиш:{" "}
                    {new Date(selected.finished_at).toLocaleString("ru-RU")}
                  </>
                )}
              </div>
            </div>

            <div style={{
              display: "grid", gridTemplateColumns: "repeat(5, 1fr)",
              gap: 8, marginBottom: 14,
            }}>
              <Stat label="Отправлено" value={p.run_sent ?? 0}
                    sub={`/ ${p.run_limit || "∞"}`} color="#16a34a" />
              <Stat label="Тестовых" value={p.test ?? 0} color="#f59e0b" />
              <Stat label="Failed" value={p.failed ?? 0} color="#dc2626" />
              <Stat label="Unknown" value={p.unknown ?? 0} color="#a855f7" />
              <Stat label="Skipped" value={p.skipped ?? 0} color="#6b7280" />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap",
                          marginBottom: 14 }}>
              <button className="btn-primary"
                      style={{ background: "#16a34a", borderColor: "#16a34a" }}
                      disabled={isActive || offline || !!status?.current_run_id}
                      onClick={handleStartClick}>
                ▶ {isActive ? "Выполняется" : (
                  (p.run_sent ?? 0) > 0 ? "Продолжить" : "Старт"
                )}
              </button>
              <button className="btn-cancel"
                      style={{ color: "#dc2626", borderColor: "#fecaca" }}
                      disabled={!isActive || offline}
                      onClick={handleStop}>
                ⏹ Стоп
              </button>
              <button className="btn-cancel"
                      onClick={() => setLogRunId(selected.id)}
                      style={{ color: "#0891b2", borderColor: "#bae6fd" }}>
                📜 Журнал
              </button>
              <button className="btn-cancel" onClick={handleReset}
                      disabled={isActive || offline}>
                🔄 Сброс
              </button>
              <button className="btn-cancel" onClick={onShowPlan}
                      disabled={offline}>
                🔍 План
              </button>
              <div style={{ flex: 1 }} />
              <button className="btn-cancel"
                      style={{ color: "#dc2626", borderColor: "#fecaca" }}
                      disabled={isActive || offline}
                      onClick={handleDelete}>
                🗑 Удалить
              </button>
            </div>

            {progress && Object.keys(progress.worker_limits || {}).length > 0 && (
              <details style={{ marginBottom: 12 }}>
                <summary style={{ cursor: "pointer", color: "#6b7280",
                                  fontSize: 12 }}>
                  🎯 Сохранённые лимиты воркеров (per-block)
                </summary>
                <pre style={{
                  background: "#f9fafb", padding: 8, borderRadius: 6,
                  fontSize: 11, marginTop: 6, maxHeight: 150,
                  overflow: "auto", fontFamily: "Consolas, monospace",
                }}>
                  {JSON.stringify(progress.worker_limits, null, 2)}
                </pre>
              </details>
            )}

            {progress && Object.keys(progress.block_limits || {}).length > 0 && (
              <div style={{ fontSize: 12, color: "#6b7280",
                            background: "#fef9c3", padding: "8px 12px",
                            borderRadius: 8, marginBottom: 12 }}>
                ♻️ Лимиты блоков:{" "}
                {Object.entries(progress.block_limits)
                  .sort((a, b) => +a[0] - +b[0])
                  .map(([k, v]) => `#${k}=${v}`).join(", ")}
              </div>
            )}
          </>
        )}
      </div>

      {resumeInfo && (
        <SenderResumeDialog
          progress={resumeInfo}
          runName={selected?.template_name || selected?.template_id || "run"}
          onChoose={handleResumeChoice}
        />
      )}

      {logRun && (
        <RunLogDialog
          run={logRun}
          runtimes={runtimes || []}
          onClose={() => setLogRunId(null)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div style={{
      padding: "8px 10px", borderRadius: 8, background: "#fff",
      border: `1px solid #e5e7eb`, borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ fontSize: 10, color: "#6b7280",
                    textTransform: "uppercase", letterSpacing: ".03em" }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>
        {value}
        {sub && (
          <span style={{ fontSize: 11, color: "#9ca3af",
                         fontWeight: 400, marginLeft: 4 }}>
            {sub}
          </span>
        )}
      </div>
    </div>
  );
}